
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { UserProvider } from "@/contexts/UserContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import BrandProtectedRoute from "@/components/BrandProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { initializeDemoOptimizations } from "@/utils/demoOptimization";
import { useEffect, lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

// Eager loaded components (critical for initial render)
import Signup from "./pages/Signup";
import Signin from "./pages/Signin";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy loaded components (code splitting)
// Admin & Analytics (heavy components)
const SimplifiedBrandAdmin = lazy(() => import("./pages/SimplifiedBrandAdmin"));
const BrandAnalyticsDashboard = lazy(() => import("./pages/BrandAnalyticsDashboard"));
const RBACDashboard = lazy(() => import("./pages/RBACDashboard"));
const BrandSettings = lazy(() => import("./pages/BrandSettings"));
const CheckInHistory = lazy(() => import("./pages/CheckInHistory"));
const SenseAnalyticsDashboard = lazy(() => import("./pages/SenseAnalyticsDashboard"));

// Brand User Pages
const BrandLogin = lazy(() => import("./pages/BrandLogin"));
const BrandUserAuth = lazy(() => import("./pages/BrandUserAuth"));
const BrandUserDashboard = lazy(() => import("./pages/BrandUserDashboard"));
const BrandedAICoach = lazy(() => import("./pages/BrandedAICoach"));
const BrandedCheckIn = lazy(() => import("./pages/BrandedCheckIn"));
const BrandedProfile = lazy(() => import("./pages/BrandedProfile"));
const BrandedOnboarding = lazy(() => import("./pages/BrandedOnboarding"));
const HealthAssessmentPage = lazy(() => import("./pages/HealthAssessmentPage"));
const HealthInsightsDashboard = lazy(() => import("./pages/HealthInsightsDashboard"));
const SenseFlow = lazy(() => import("./pages/SenseFlow"));
const SpanFlow = lazy(() => import("./pages/SpanFlow"));

// Store & Subscriptions
const BrandStore = lazy(() => import("./pages/BrandStore"));
const BonafideSubscription = lazy(() => import("./pages/BonafideSubscription"));

// Utilities
const HomeRedirect = lazy(() => import("./pages/HomeRedirect"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const AppContent = () => {
  useEffect(() => {
    initializeDemoOptimizations();
  }, []);

  // Environment-based route configuration
  const isDevelopment = import.meta.env.DEV;
  const isProduction = import.meta.env.PROD;

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Authentication */}
        <Route path="/signup" element={<Signup />} />
        <Route path="/signin" element={<Signin />} />
        
        {/* Landing Page */}
        <Route path="/" element={<Index />} />
        
        {/* Preview Routes - Only available in development or staging */}
        {!isProduction && (
          <>
            <Route path="/preview/:brandKey/check-in" element={<BrandedCheckIn />} />
            <Route path="/preview/:brandKey/sense-flow" element={<SenseFlow />} />
            <Route path="/preview/:brandKey/span-flow" element={<SpanFlow />} />
            <Route path="/preview/:brandKey/ai-coach" element={<BrandedAICoach />} />
            <Route path="/preview/:brandKey/profile" element={<BrandedProfile />} />
            <Route path="/preview/:brandKey/onboarding" element={<BrandedOnboarding />} />
            <Route path="/preview/:brandKey/subscription" element={<BonafideSubscription />} />
            <Route path="/preview/:brandKey/store" element={<BrandStore />} />
          </>
        )}
        
        {/* Protected Dashboard */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <HomeRedirect />
          </ProtectedRoute>
        } />
        
        {/* Health Assessment Onboarding */}
        <Route path="/onboarding/health-assessment" element={
          <ProtectedRoute>
            <HealthAssessmentPage />
          </ProtectedRoute>
        } />
        
        {/* Health Insights Dashboard */}
        <Route path="/health-insights" element={
          <ProtectedRoute>
            <HealthInsightsDashboard />
          </ProtectedRoute>
        } />
        
        {/* Check-in History */}
        <Route path="/check-in-history" element={
          <ProtectedRoute>
            <CheckInHistory />
          </ProtectedRoute>
        } />
        
        {/* SENSE Analytics */}
        <Route path="/sense-analytics" element={
          <ProtectedRoute>
            <SenseAnalyticsDashboard />
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requireSuperAdmin>
            <SimplifiedBrandAdmin />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <ProtectedRoute>
            <BrandAnalyticsDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/rbac" element={
          <ProtectedRoute requireSuperAdmin>
            <RBACDashboard />
          </ProtectedRoute>
        } />
        
        {/* Brand Configuration */}
        <Route path="/brand/:brandId/settings" element={
          <ProtectedRoute>
            <BrandSettings />
          </ProtectedRoute>
        } />
        
        
        {/* Production Brand User Routes */}
        <Route path="/brand/:brandSlug/login" element={<BrandLogin />} />
        <Route path="/brand/:brandSlug/auth" element={<BrandUserAuth />} />
        <Route path="/brand/:brandSlug" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <BrandUserDashboard />
          </BrandProtectedRoute>
        } />
        <Route path="/brand/:brandSlug/check-in" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <BrandedCheckIn />
          </BrandProtectedRoute>
        } />
        <Route path="/brand/:brandSlug/sense-flow" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <SenseFlow />
          </BrandProtectedRoute>
        } />
        <Route path="/brand/:brandSlug/span-flow" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <SpanFlow />
          </BrandProtectedRoute>
        } />
        <Route path="/brand/:brandSlug/ai-coach" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <BrandedAICoach />
          </BrandProtectedRoute>
        } />
        <Route path="/brand/:brandSlug/profile" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <BrandedProfile />
          </BrandProtectedRoute>
        } />
        <Route path="/brand/:brandSlug/onboarding" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <BrandedOnboarding />
          </BrandProtectedRoute>
        } />
        <Route path="/brand/:brandSlug/subscription" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <BonafideSubscription />
          </BrandProtectedRoute>
        } />
        <Route path="/brand/:brandSlug/store" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <BrandStore />
          </BrandProtectedRoute>
        } />
        <Route path="/brand/:brandSlug/sense-analytics" element={
          <BrandProtectedRoute requireBrandUser={false}>
            <SenseAnalyticsDashboard />
          </BrandProtectedRoute>
        } />
        
          {/* Catch-all Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <BrandProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </TooltipProvider>
          </BrandProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
